import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, Pencil, Trash2, Wrench, X, Search, Paperclip, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import CondominioSearchSelect from "../components/CondominioSearchSelect";
import ExportExcelButton from "../components/ExportExcelButton";

const TIPOS = [
  "Orçamento",
  "Ocorrência",
  "Contacto Telefónico",
  "Email",
  "Processo de Rehabilitação",
  "Processo de Rehabilitação Light",
  "Processo de Rehabilitação Compacto",
  "Fiscalização e Coordenação de Segurança em Obra",
  "Vistoria",
  "Processo Judicial",
  "Concurso Lançado",
  "Mapa Comparativo enviado",
  "Assembleia",
  "Revisão de Projeto",
  "Revisão de Mapa Comparativo",
  "Pedido de Plantas",
  "Contacto Comercial",
];
const ESTADOS = ["Agendado", "Em Curso", "Concluído", "Cancelado"];

const EMPTY = {
  condominio_id: "",
  tipo: "Orçamento",
  descricao: "",
  data: new Date().toISOString().slice(0, 10),
  valor: 0,
  estado: "Agendado",
  tecnico: "",
};

export default function Servicos() {
  const [items, setItems] = useState([]);
  const [condominios, setCondominios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filterCond, setFilterCond] = useState("");
  const [filterEmpresa, setFilterEmpresa] = useState("");
  const [search, setSearch] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");

  async function loadAttachments(sid) {
    if (!sid) return setAttachments([]);
    try {
      const { data } = await api.get(`/servicos/${sid}/attachments`);
      setAttachments(data);
    } catch {
      setAttachments([]);
    }
  }

  useEffect(() => {
    if (editing?.id) loadAttachments(editing.id);
    else setAttachments([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  async function refresh() {
    setLoading(true);
    try {
      const [s, c, e] = await Promise.all([
        api.get("/servicos", { params: filterCond ? { condominio_id: filterCond } : {} }),
        api.get("/condominios"),
        api.get("/empresas-condominio"),
      ]);
      setItems(s.data);
      setCondominios(c.data);
      setEmpresas(e.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [filterCond]);

  const condMap = Object.fromEntries(condominios.map((c) => [c.id, c.nome]));
  const condEmpresaMap = Object.fromEntries(
    condominios.map((c) => [c.id, c.empresa_condominio_id || ""])
  );
  const condEmpresaNomeMap = Object.fromEntries(
    condominios.map((c) => [c.id, c.empresa_condominio_nome || ""])
  );

  const filtered = items.filter((s) => {
    if (filterEmpresa && condEmpresaMap[s.condominio_id] !== filterEmpresa) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const cn = (condMap[s.condominio_id] || "").toLowerCase();
    const en = (condEmpresaNomeMap[s.condominio_id] || "").toLowerCase();
    return (
      cn.includes(q) ||
      en.includes(q) ||
      (s.tipo || "").toLowerCase().includes(q) ||
      (s.descricao || "").toLowerCase().includes(q)
    );
  });

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY, condominio_id: condominios[0]?.id || "" });
    setOpenForm(true);
  }

  function openEdit(item) {
    setEditing(item);
    setForm({ ...item });
    setOpenForm(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.condominio_id) return toast.error("Selecione um condomínio");
    setSaving(true);
    try {
      const payload = { ...form, valor: Number(form.valor) || 0 };
      if (editing) {
        const { data } = await api.put(`/servicos/${editing.id}`, payload);
        setEditing(data);
        toast.success("Serviço atualizado");
      } else {
        const { data } = await api.post("/servicos", payload);
        setEditing(data);
        setForm(data);
        toast.success("Serviço criado — pode agora anexar ficheiros");
      }
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setSaving(false);
    }
  }

  async function onUploadFile(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length || !editing?.id) return;
    // Validate all first
    for (const f of files) {
      if (f.size > 100 * 1024 * 1024) {
        toast.error(`"${f.name}" ultrapassa o limite de 100 MB`);
        e.target.value = "";
        return;
      }
    }
    setUploading(true);
    let okCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fd = new FormData();
      fd.append("file", file);
      setUploadFileName(`${file.name}  (${i + 1}/${files.length})`);
      setUploadProgress(0);
      try {
        await api.post(`/servicos/${editing.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) {
              setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
            }
          },
        });
        okCount++;
      } catch (er) {
        toast.error(
          `Falha em "${file.name}": ${formatApiErrorDetail(er.response?.data?.detail) || er.message}`
        );
      }
    }
    await loadAttachments(editing.id);
    if (okCount > 0) {
      toast.success(
        okCount === files.length
          ? `${okCount} ficheiro(s) anexado(s)`
          : `${okCount}/${files.length} ficheiros anexados`
      );
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadFileName("");
    e.target.value = "";
  }

  async function onDownloadAttachment(att) {
    try {
      const resp = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Não foi possível descarregar o ficheiro");
    }
  }

  async function onDeleteAttachment(att) {
    if (!window.confirm(`Eliminar ficheiro "${att.filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      await loadAttachments(editing.id);
      toast.success("Ficheiro eliminado");
    } catch {
      toast.error("Erro ao eliminar ficheiro");
    }
  }

  async function onDelete(item) {
    if (!window.confirm("Eliminar este serviço?")) return;
    try {
      await api.delete(`/servicos/${item.id}`);
      toast.success("Serviço eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="space-y-6" data-testid="servicos-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input
              data-testid="servicos-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar por condomínio, tipo ou descrição…"
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-zinc-200 rounded-sm text-sm font-manrope focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
            />
          </div>
          <select
            data-testid="servicos-filter-empresa"
            value={filterEmpresa}
            onChange={(e) => setFilterEmpresa(e.target.value)}
            className="bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm text-neutral-900 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none min-w-[240px]"
          >
            <option value="">Todas as administrações</option>
            {empresas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>
          <select
            data-testid="servicos-filter-cond"
            value={filterCond}
            onChange={(e) => setFilterCond(e.target.value)}
            className="bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm text-neutral-900 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none min-w-[220px]"
          >
            <option value="">Todos os condomínios</option>
            {condominios
              .filter((c) => !filterEmpresa || c.empresa_condominio_id === filterEmpresa)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
          </select>
          {(filterEmpresa || filterCond || search) && (
            <button
              data-testid="servicos-clear-filters"
              type="button"
              onClick={() => {
                setFilterEmpresa("");
                setFilterCond("");
                setSearch("");
              }}
              className="text-xs font-manrope text-neutral-500 hover:text-[#0047FF] underline underline-offset-2 transition-colors"
            >
              Limpar filtros
            </button>
          )}
        </div>
        <button
          data-testid="servicos-new-btn"
          onClick={openCreate}
          disabled={condominios.length === 0}
          className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-5 py-2.5 font-manrope font-semibold text-sm transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Novo serviço
        </button>
        <ExportExcelButton
          endpoint="/servicos/export/xlsx"
          filename="registos_servicos.xlsx"
          params={{
            ...(filterCond ? { condominio_id: filterCond } : {}),
            ...(filterEmpresa && !filterCond ? { empresa_condominio_id: filterEmpresa } : {}),
          }}
          testid="export-servicos-btn"
        />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm font-manrope text-neutral-500">A carregar…</div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <Wrench className="w-8 h-8 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
            <div className="text-sm font-manrope text-neutral-500">
              {condominios.length === 0
                ? "Registe primeiro um condomínio para adicionar serviços."
                : items.length === 0
                ? "Nenhum serviço registado."
                : "Sem resultados para a pesquisa."}
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50/50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
                <th className="py-3 px-6 text-left">Data</th>
                <th className="py-3 px-6 text-left">Condomínio</th>
                <th className="py-3 px-6 text-left">Administração</th>
                <th className="py-3 px-6 text-left">Tipo</th>
                <th className="py-3 px-6 text-left">Descrição</th>
                <th className="py-3 px-6 text-right">Valor</th>
                <th className="py-3 px-6 text-left">Estado</th>
                <th className="py-3 px-6 text-left">Registado por</th>
                <th className="py-3 px-6 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`servico-row-${s.id}`}>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700 font-mono">{s.data}</td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-950 font-semibold">
                    {condMap[s.condominio_id] || "—"}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">
                    {condEmpresaNomeMap[s.condominio_id] || <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">{s.tipo}</td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-600 max-w-xs truncate">{s.descricao}</td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-950 font-semibold text-right">
                    €{Number(s.valor).toLocaleString("pt-PT", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-4 px-6">
                    <EstadoChip s={s.estado} />
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">
                    {s.created_by_name || s.created_by_email ? (
                      <div>
                        <div>{s.created_by_name || "—"}</div>
                        {s.created_by_email && (
                          <div className="text-xs text-neutral-500 font-mono">{s.created_by_email}</div>
                        )}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`edit-${s.id}`}
                        onClick={() => openEdit(s)}
                        className="p-2 text-neutral-500 hover:text-[#0047FF] hover:bg-[#0047FF]/5 rounded-sm"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button
                        data-testid={`delete-${s.id}`}
                        onClick={() => onDelete(s)}
                        className="p-2 text-neutral-500 hover:text-[#FF4500] hover:bg-[#FF4500]/5 rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openForm && (
        <Modal onClose={() => setOpenForm(false)} title={editing ? "Editar serviço" : "Novo serviço"}>
          <form onSubmit={onSubmit} className="space-y-5" data-testid="servicos-form">
            <CondominioSearchSelect
              value={form.condominio_id}
              onChange={(v) => setForm({ ...form, condominio_id: v })}
              condominios={condominios}
              testid="input-cond"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <SelectField label="Tipo *" testid="input-tipo" value={form.tipo} onChange={(v) => setForm({ ...form, tipo: v })} options={TIPOS.map((t) => ({ value: t, label: t }))} />
              <SelectField label="Estado *" testid="input-estado" value={form.estado} onChange={(v) => setForm({ ...form, estado: v })} options={ESTADOS.map((t) => ({ value: t, label: t }))} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <TextField label="Data *" testid="input-data" type="date" required value={form.data} onChange={(v) => setForm({ ...form, data: v })} />
              <TextField label="Valor (€) *" testid="input-valor" type="number" required value={form.valor} onChange={(v) => setForm({ ...form, valor: v })} step="0.01" />
            </div>
            {form.tipo === "Processo de Rehabilitação" && (
              <TextField
                label="Nome do técnico *"
                testid="input-tecnico"
                value={form.tecnico}
                onChange={(v) => setForm({ ...form, tecnico: v })}
                placeholder="Ex.: Eng. António Silva"
                required
              />
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">Descrição</label>
              <textarea
                data-testid="input-descricao"
                value={form.descricao || ""}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                rows={3}
                className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none resize-none"
              />
            </div>

            <div className="border-t border-zinc-200 pt-5">
              <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-chivo font-bold mb-4">
                Ficheiros anexos
              </div>
              {editing?.id ? (
                <>
                  <label
                    data-testid="upload-file-label"
                    className={`inline-flex items-center gap-2 border rounded-sm px-4 py-2 font-manrope font-medium text-sm cursor-pointer transition-colors mb-3 ${
                      uploading
                        ? "border-zinc-200 text-neutral-400 cursor-not-allowed"
                        : "bg-white border-zinc-200 text-neutral-900 hover:border-[#0047FF] hover:text-[#0047FF]"
                    }`}
                  >
                    <Upload className="w-4 h-4" strokeWidth={1.5} />
                    {uploading ? `A carregar… ${uploadProgress}%` : "Anexar ficheiros"}
                    <input
                      type="file"
                      multiple
                      onChange={onUploadFile}
                      className="hidden"
                      disabled={uploading}
                      data-testid="upload-file-input"
                    />
                  </label>
                  {uploading && (
                    <div
                      data-testid="upload-progress"
                      className="mb-3 border border-zinc-200 rounded-sm p-3 bg-zinc-50/50"
                    >
                      <div className="flex items-center justify-between mb-1.5 gap-3">
                        <div className="text-xs font-manrope text-neutral-700 truncate flex-1">
                          {uploadFileName}
                        </div>
                        <div className="text-xs font-mono text-neutral-500 shrink-0">
                          {uploadProgress}%
                        </div>
                      </div>
                      <div className="h-1.5 bg-zinc-200 rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-[#0047FF] transition-[width] duration-150 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                          data-testid="upload-progress-bar"
                        />
                      </div>
                      {uploadProgress === 100 && (
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold mt-2">
                          A processar no servidor…
                        </div>
                      )}
                    </div>
                  )}
                  {attachments.length === 0 ? (
                    <div className="text-sm font-manrope text-neutral-500 py-2">Sem ficheiros anexados.</div>
                  ) : (
                    <ul className="space-y-2" data-testid="attachments-list">
                      {attachments.map((a) => (
                        <li
                          key={a.id}
                          data-testid={`attachment-${a.id}`}
                          className="flex items-center justify-between gap-3 border border-zinc-200 rounded-sm px-3 py-2 hover:border-zinc-300 transition-colors"
                        >
                          <button
                            type="button"
                            onClick={() => onDownloadAttachment(a)}
                            data-testid={`download-${a.id}`}
                            className="flex items-center gap-3 flex-1 min-w-0 text-left group"
                          >
                            <Paperclip className="w-4 h-4 text-neutral-500 flex-shrink-0" strokeWidth={1.5} />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-manrope font-medium text-neutral-950 truncate group-hover:text-[#0047FF] transition-colors">
                                {a.filename}
                              </div>
                              <div className="text-[11px] text-neutral-500 font-manrope">
                                {formatSize(a.size)} · {a.content_type}
                              </div>
                            </div>
                            <Download className="w-4 h-4 text-neutral-400 flex-shrink-0 group-hover:text-[#0047FF] transition-colors" strokeWidth={1.5} />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteAttachment(a)}
                            data-testid={`delete-att-${a.id}`}
                            className="p-1.5 text-neutral-400 hover:text-[#FF4500] rounded-sm"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <div className="text-sm font-manrope text-neutral-500 py-2">
                  Guarde o serviço primeiro para anexar ficheiros.
                </div>
              )}
            </div>
            <FormActions onCancel={() => setOpenForm(false)} saving={saving} editing={editing} />
          </form>
        </Modal>
      )}
    </div>
  );
}

function EstadoChip({ s }) {
  const map = {
    Agendado: "bg-[#0047FF]/10 text-[#0047FF] border-[#0047FF]/20",
    "Em Curso": "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30",
    "Concluído": "bg-[#16A34A]/10 text-[#15803D] border-[#16A34A]/30",
    Cancelado: "bg-zinc-100 text-neutral-600 border-zinc-200",
  };
  return (
    <span className={`inline-block text-[10px] font-chivo font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${map[s] || map.Cancelado}`}>
      {s}
    </span>
  );
}

function formatSize(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
          <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight">{title}</h2>
          <button onClick={onClose} data-testid="close-modal" className="p-2 text-neutral-400 hover:text-neutral-900 rounded-sm hover:bg-zinc-100">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-8">{children}</div>
      </div>
    </div>
  );
}

export function TextField({ label, value, onChange, required, type = "text", testid, step, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">{label}</label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        step={step}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
      />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">{label}</label>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
      >
        <option value="">— Selecionar —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function FormActions({ onCancel, saving, editing }) {
  return (
    <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-200">
      <button
        type="button"
        data-testid="cancel-btn"
        onClick={onCancel}
        className="bg-white text-neutral-900 border border-zinc-200 hover:bg-zinc-50 rounded-sm px-5 py-2.5 font-manrope font-medium text-sm"
      >
        Cancelar
      </button>
      <button
        type="submit"
        data-testid="save-btn"
        disabled={saving}
        className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-6 py-2.5 font-manrope font-semibold text-sm disabled:opacity-60"
      >
        {saving ? "A guardar…" : editing ? "Guardar alterações" : "Criar"}
      </button>
    </div>
  );
}
